import type { ICellRendererComp, ICellRendererParams } from 'ag-grid-community'
import type { BadgeData } from '../utils/dom'
import { iconElement } from '../utils/dom'

/**
 * Badge value item structure from backend
 * Contains full badge metadata including color and icon
 */
type BadgeValueItem = {
  readonly value: string | number | boolean
  readonly label: string
  readonly icon?: string
  readonly color?: string
}

/**
 * Find badge data by label from badge values array
 * Pure function that maps label string to full badge data
 */
const findBadgeByLabel = (
  label: string,
  badgeValues: readonly BadgeValueItem[]
): BadgeData => {
  const matchingBadge = badgeValues.find(
    (item) => item.label === label || String(item.value) === label
  )

  if (matchingBadge) {
    return {
      label: matchingBadge.label,
      color: matchingBadge.color,
      icon: matchingBadge.icon
    }
  }

  // Fallback for unmatched labels (shouldn't happen with valid data)
  return { label }
}

/**
 * Simple Badge Dropdown Renderer
 * Renders badge options in rich select dropdown with minimal styling
 * Shows just icon + text without colored badge background/borders
 *
 * This provides a cleaner dropdown experience when the column is full of badges,
 * avoiding visual clutter from multiple colored badges in the dropdown list.
 *
 * Architecture:
 * - Receives label string as params.value (for AG Grid selection)
 * - Subclasses override getBadgeValues() to provide metadata via closure
 * - Looks up badge data by label and renders simple icon + text
 *
 * Implementation follows AG Grid ICellRendererComp interface
 */
export class SimpleBadgeDropdownRenderer implements ICellRendererComp {
  private eGui: HTMLElement | undefined

  /**
   * Override in subclass to provide badge values
   * Default implementation returns empty array
   */
  getBadgeValues(): readonly BadgeValueItem[] {
    return []
  }

  /**
   * Initialize renderer with simple icon + text element
   * Called by AG Grid when dropdown item is created
   */
  init(params: ICellRendererParams): void {
    const label =
      typeof params.value === 'string'
        ? params.value
        : String(params.value ?? '')
    const badgeValues = this.getBadgeValues()

    // Look up badge data by label
    const badgeData =
      badgeValues.length > 0 ? findBadgeByLabel(label, badgeValues) : { label }

    // Create simple container with flexbox layout
    const container = document.createElement('div')
    container.style.display = 'flex'
    container.style.alignItems = 'center'
    container.style.gap = '8px'
    container.style.padding = '4px 0'

    // Add icon if available — plain light-DOM markup, no <facade-icon> upgrade.
    if (badgeData.icon) {
      const icon = iconElement(badgeData.icon, 'sm')
      container.appendChild(icon)
    }

    // Add text label
    const textSpan = document.createElement('span')
    textSpan.textContent = badgeData.label || ''
    container.appendChild(textSpan)

    this.eGui = container
  }

  /**
   * Return the DOM element for AG Grid to render
   * Called by AG Grid to get the rendered component
   */
  getGui(): HTMLElement {
    return this.eGui || document.createElement('div')
  }

  /**
   * Handle value updates
   * Returns false to trigger full rebuild instead of partial update
   */
  refresh(_params: ICellRendererParams): boolean {
    return false
  }

  /**
   * Cleanup when renderer is destroyed
   * Called by AG Grid when dropdown item is removed
   */
  destroy(): void {
    this.eGui = undefined
  }
}

// Export types for external use
export type { BadgeValueItem }
